import { Tooltip as BaseTooltip } from '@base-ui/react/tooltip'
import {
  createContext,
  type ReactElement,
  useContext,
  useId,
  useState,
} from 'react'
import { cx } from '../cx'
import { useOverlayLayer } from '../overlay/escapeStack'
import { overlayClass } from '../overlay/Overlay'
import {
  type Placement,
  spaceOffset,
  toSideAlign,
} from '../overlay/positioning'
import styles from './Tooltip.module.css'

// Wraps Base UI Tooltip. Deviations: role="tooltip" and aria-describedby on the
// trigger while open (Tooltip card; Base UI sets neither); Escape via the
// page-wide stack (a non-blocking layer: it takes Escape but never holds off
// app shortcuts); a Tooltip around a trigger that brings its own (IconButton)
// replaces that one. Tested.

const InTrigger = createContext(false)

type TooltipProps = {
  content: string
  children: ReactElement
  placement?: Placement
  delay?: number
}

export function Tooltip(props: TooltipProps) {
  return useContext(InTrigger) ? props.children : <TooltipRoot {...props} />
}

function TooltipRoot({
  content,
  children,
  placement = 'top',
  delay = 500,
}: TooltipProps) {
  const [open, setOpen] = useState(false)
  const onOpenChange = useOverlayLayer(open, (next) => setOpen(next), {
    blocking: false,
  })
  const id = useId()
  const { side, align } = toSideAlign(placement)

  return (
    <BaseTooltip.Root open={open} onOpenChange={onOpenChange}>
      <InTrigger.Provider value={true}>
        <BaseTooltip.Trigger
          render={children}
          delay={delay}
          closeDelay={0}
          aria-describedby={open ? id : undefined}
        />
      </InTrigger.Provider>
      <BaseTooltip.Portal>
        <BaseTooltip.Positioner
          side={side}
          align={align}
          sideOffset={spaceOffset('space-4')}
          className={styles.positioner}
        >
          <BaseTooltip.Popup
            id={id}
            role="tooltip"
            className={cx(overlayClass.surface, styles.popup)}
            data-weight="sm"
          >
            {content}
          </BaseTooltip.Popup>
        </BaseTooltip.Positioner>
      </BaseTooltip.Portal>
    </BaseTooltip.Root>
  )
}
